import { Calendar, Edit3, Mail, MapPin, Phone, Save, User } from "lucide-react";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { AvatarUploadBox } from "../../components/profile/AvatarUploadBox";
import { PageBanner } from "../../components/ui/PageBanner";
import { userApi } from "../../services/api/userApi";
import { useAuthStore } from "../../stores/authStore";
import { getCountryName } from "../../data/countries";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { devPersistenceGuard } from "../../utils/persistenceGuard";
import { log } from "@/lib/log";

// Nationality codes as stored in the database
const NATIONALITIES = [
  "german",
  "austrian",
  "swiss",
  "french",
  "italian",
  "spanish",
  "british",
  "american",
  "canadian",
  "dutch",
  "belgian",
  "polish",
  "turkish",
  "russian",
  "other",
];

// State/province lists per country. The keys are the stored country values
// (German names, kept for existing data); the entries are proper names stored
// as they are.
const STATE_OPTIONS: { [key: string]: string[] } = {
  Deutschland: [
    "Baden-Württemberg",
    "Bayern",
    "Berlin",
    "Brandenburg",
    "Bremen",
    "Hamburg",
    "Hessen",
    "Mecklenburg-Vorpommern",
    "Niedersachsen",
    "Nordrhein-Westfalen",
    "Rheinland-Pfalz",
    "Saarland",
    "Sachsen",
    "Sachsen-Anhalt",
    "Schleswig-Holstein",
    "Thüringen",
  ],
  Österreich: [
    "Burgenland",
    "Kärnten",
    "Niederösterreich",
    "Oberösterreich",
    "Salzburg",
    "Steiermark",
    "Tirol",
    "Vorarlberg",
    "Wien",
  ],
  Schweiz: [
    "Aargau",
    "Appenzell Ausserrhoden",
    "Appenzell Innerrhoden",
    "Basel-Landschaft",
    "Basel-Stadt",
    "Bern",
    "Freiburg",
    "Genf",
    "Glarus",
    "Graubünden",
    "Jura",
    "Luzern",
    "Neuenburg",
    "Nidwalden",
    "Obwalden",
    "Schaffhausen",
    "Schwyz",
    "Solothurn",
    "St. Gallen",
    "Tessin",
    "Thurgau",
    "Uri",
    "Waadt",
    "Wallis",
    "Zug",
    "Zürich",
  ],
  USA: [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ],
  Kanada: [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Northwest Territories",
    "Nova Scotia",
    "Nunavut",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Yukon",
  ],
};

/*
 * Countries offered in the profile. `value` is what is stored (German names,
 * kept for existing data); the label comes from the shared country list in the
 * app language.
 */
const COUNTRY_OPTIONS: { value: string; code: string | null }[] = [
  { value: "Deutschland", code: "DE" },
  { value: "Österreich", code: "AT" },
  { value: "Schweiz", code: "CH" },
  { value: "Frankreich", code: "FR" },
  { value: "Italien", code: "IT" },
  { value: "Spanien", code: "ES" },
  { value: "Großbritannien", code: "GB" },
  { value: "USA", code: "US" },
  { value: "Kanada", code: "CA" },
  { value: "Niederlande", code: "NL" },
  { value: "Belgien", code: "BE" },
  { value: "Polen", code: "PL" },
  { value: "Türkei", code: "TR" },
  { value: "Russland", code: "RU" },
  { value: "Andere", code: null },
];

/** Display label for a stored country value; unknown values show as stored. */
export function profileCountryLabel(
  value: string,
  t: Translation,
  language: Parameters<typeof getCountryName>[1],
): string {
  const option = COUNTRY_OPTIONS.find((o) => o.value === value);
  if (!option) return value;
  return option.code ? getCountryName(option.code, language) : t.other;
}

/**
 * Splits a stored date of birth into its calendar parts. A bare or UTC ISO
 * value is read from the string itself: parsing it with `new Date()` and then
 * reading local fields shifts it a day back west of UTC.
 */
function birthDateParts(value: string | Date | null | undefined): {
  iso: string;
  day: string;
  month: string;
  year: string;
} {
  const empty = { iso: "", day: "", month: "", year: "" };
  if (!value) return empty;
  let iso = "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    iso = value.slice(0, 10);
  } else {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return empty;
    iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  const [year = "", month = "", day = ""] = iso.split("-");
  return { iso, day, month, year };
}

/** Order of day, month and year in a numeric date of the given locale. */
function dateFieldOrder(locale: string): ("day" | "month" | "year")[] {
  try {
    const order = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
      .formatToParts(new Date(2000, 10, 22))
      .map((p) => p.type)
      .filter(
        (type): type is "day" | "month" | "year" =>
          type === "day" || type === "month" || type === "year",
      );
    if (order.length === 3) return order;
  } catch {
    // fall through to the day-first default
  }
  return ["day", "month", "year"];
}

// User-facing hint per field for a validation error from the API
const getErrorSolution = (
  fieldName: string,
  errorMessage: string,
  t: Translation,
): string => {
  const solutions: { [key: string]: string } = {
    firstName: t.profErrFirstName,
    lastName: t.profErrLastName,
    phoneNumber: t.profErrPhone,
    dateOfBirth: t.profErrDateOfBirth,
    birthDay: t.profErrBirthDay,
    birthMonth: t.profErrBirthMonth,
    birthYear: t.profErrBirthYear,
    nationality: t.profErrNationality,
    address: t.profErrAddress,
    city: t.profErrCity,
    country: t.profErrCountry,
    postalCode: t.profErrPostalCode,
  };

  return (
    solutions[fieldName] ||
    t.profErrCheckField
      .replace("{field}", fieldName)
      .replace("{message}", errorMessage)
  );
};

export const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const { t, language } = useI18nStore();
  const fmt = useLocaleFormat();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState(() => {
    log.debug("Initializing profileData with user:", user);

    // Split the stored address into street and house number
    const addressParts = user?.address ? user.address.split(" ") : [];
    const street = addressParts.slice(0, -1).join(" ") || "";
    const houseNumber =
      addressParts.length > 1 ? addressParts[addressParts.length - 1] : "";

    return {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phoneNumber || "",
      dateOfBirth: birthDateParts(user?.dateOfBirth).iso,
      // Separate date-of-birth fields
      birthDay: birthDateParts(user?.dateOfBirth).day,
      birthMonth: birthDateParts(user?.dateOfBirth).month,
      birthYear: birthDateParts(user?.dateOfBirth).year,
      nationality: user?.nationality || "",
      gender: user?.gender || "",
      street: street,
      houseNumber: houseNumber,
      addressSupplement: "",
      postalCode: user?.postalCode || "",
      city: user?.city || "",
      country: user?.country || "",
      state: user?.state || "",
    };
  });

  // Update profileData when user changes (needed so saved data shows up)
  React.useEffect(() => {
    if (user) {
      log.debug("User data updated, refreshing profileData:", user);

      const addressParts = user.address ? user.address.split(" ") : [];
      const street = addressParts.slice(0, -1).join(" ") || "";
      const houseNumber =
        addressParts.length > 1 ? addressParts[addressParts.length - 1] : "";

      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phoneNumber || "",
        dateOfBirth: birthDateParts(user.dateOfBirth).iso,
        birthDay: birthDateParts(user.dateOfBirth).day,
        birthMonth: birthDateParts(user.dateOfBirth).month,
        birthYear: birthDateParts(user.dateOfBirth).year,
        nationality: user.nationality || "",
        gender: user.gender || "",
        street: street,
        houseNumber: houseNumber,
        addressSupplement: "",
        postalCode: user.postalCode || "",
        city: user.city || "",
        country: user.country || "",
        state: user.state || "",
      });
    }
  }, [user]);

  // Persistence Guard: Verify data mapping on component mount
  React.useEffect(() => {
    devPersistenceGuard.checkProfileData(profileData, user);
  }, [profileData, user]);

  // Age in whole years from an ISO date
  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;

    const today = new Date();
    const { day, month, year } = birthDateParts(birthDate);
    let age = today.getFullYear() - Number(year);
    const monthDiff = today.getMonth() + 1 - Number(month);

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < Number(day))) {
      age--;
    }

    return age;
  };

  // Combines the date-of-birth fields into an ISO date, or "" if invalid
  const combineBirthDate = (): string => {
    const { birthDay, birthMonth, birthYear } = profileData;
    if (!birthDay || !birthMonth || !birthYear) return "";

    const day = parseInt(birthDay);
    const month = parseInt(birthMonth);
    const year = parseInt(birthYear);

    if (
      day < 1 ||
      day > 31 ||
      month < 1 ||
      month > 12 ||
      year < 1900 ||
      year > new Date().getFullYear()
    ) {
      return "";
    }

    // Reject impossible dates such as February 31
    const testDate = new Date(year, month - 1, day);
    if (
      testDate.getFullYear() !== year ||
      testDate.getMonth() !== month - 1 ||
      testDate.getDate() !== day
    ) {
      return "";
    }

    // ISO date (YYYY-MM-DD)
    return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  };

  const currentAge = combineBirthDate() ? calculateAge(combineBirthDate()) : 0;

  const handleSave = async () => {
    try {
      // Street and house number make up the address field
      const addressString = `${profileData.street}${profileData.houseNumber ? " " + profileData.houseNumber : ""}`;

      // Date of birth from the separate fields
      const combinedBirthDate = combineBirthDate();

      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phone || undefined, // empty string becomes undefined
        dateOfBirth: combinedBirthDate || undefined,
        nationality: profileData.nationality || undefined,
        gender: profileData.gender || undefined,
        address: addressString.trim() || undefined,
        postalCode: profileData.postalCode || undefined,
        city: profileData.city || undefined,
        state: profileData.state || undefined,
        country: profileData.country || undefined,
      };

      // Persistence Guard: Check update data before sending
      devPersistenceGuard.checkUpdateData(updateData, profileData);

      log.debug("Sending profile data:", updateData);
      const response = await userApi.updateProfile(updateData);
      log.debug("Received response:", response);

      // Persistence Guard: Check API response structure
      devPersistenceGuard.checkApiResponse(response);

      if (response && response.success) {
        log.debug(
          "Profile update successful, updating AuthStore with:",
          response.data.user,
        );

        toast.success(t.profileUpdatedSuccessfully);
        setIsEditing(false);

        // Update the auth store with the new user data - required for persistence
        if (response.data && response.data.user) {
          updateUser(response.data.user);

          // Verify the update worked
          setTimeout(() => {
            const currentUser = useAuthStore.getState().user;
            log.debug("AuthStore user after update:", currentUser);
          }, 100);
        } else {
          console.error("No user data in response:", response);
          toast.error(t.profileUpdatedButDataNotLoaded);
        }
      } else if (response && response.success === false) {
        throw new Error(response.message || t.profUpdateFailed);
      } else {
        // Fallback: no success property, but nothing was thrown either
        console.warn("Unexpected response shape:", response);
        toast.success(t.profileUpdateMaybeSuccessful);
        setIsEditing(false);
      }
    } catch (error: any) {
      console.error("Saving the profile failed:", {
        error,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      // Error handling by status
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            // Validation errors
            if (data?.details && Array.isArray(data.details)) {
              const validationErrors = data.details;
              const errorMessages = validationErrors
                .map(
                  (err: {
                    path?: string;
                    param?: string;
                    msg?: string;
                    message?: string;
                  }) => {
                    const field =
                      err.path || err.param || t.profErrUnknownField;
                    const message =
                      err.msg || err.message || t.profErrInvalidValue;
                    const solution = getErrorSolution(field, message, t);
                    return `• ${solution}`;
                  },
                )
                .join("\n");
              toast.error(`${t.correctFollowingInputs}\n\n${errorMessages}`, {
                duration: 10000,
              });
            } else if (data?.error) {
              toast.error(
                `${t.inputError}: ${data.error}\n\n${t.invalidInputs}`,
              );
            } else {
              toast.error(t.invalidInputs);
            }
            break;

          case 401:
            toast.error(t.unauthorizedPleaseLogin);
            break;

          case 403:
            toast.error(t.noPermission);
            break;

          case 404:
            toast.error(t.userNotFoundPleaseLogin);
            break;

          case 500:
            toast.error(t.serverErrorTryLater);
            break;

          default:
            toast.error(
              `${t.unknownError} (${status}): ${data?.error || data?.message || t.unknownError}`,
            );
        }
      } else if (error.request) {
        // Network error
        toast.error(t.noServerConnection);
      } else if (error.message) {
        // Other errors
        toast.error(`${t.errorOccurred}: ${error.message}`);
      } else {
        // Fallback
        toast.error(t.unexpectedError);
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Banner */}
      <PageBanner
        icon={User}
        title={t.profile}
        description={t.managePersonalDataSettings}
      />

      <div className="space-y-6">
        {/* Avatar Upload Box */}
        <AvatarUploadBox />

        {/* Personal Information Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {t.personalInformation}
            </h2>
            <button
              data-testid="edit-profile"
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              <span>{isEditing ? t.cancel : t.edit}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.firstName}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900 py-2">
                  {profileData.firstName || "-"}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.lastName}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900 py-2">
                  {profileData.lastName || "-"}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.email}
              </label>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{profileData.email}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.phone}
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder={t.profPhonePlaceholder}
                />
              ) : (
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-900">
                    {profileData.phone || t.notSpecified}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.dateOfBirth}
              </label>
              {isEditing ? (
                <div className="grid grid-cols-3 gap-2">
                  {dateFieldOrder(fmt.locale).map((field) => (
                    <div key={field}>
                      {field === "day" && (
                        <select
                          name="birthDay"
                          value={profileData.birthDay}
                          onChange={(e) =>
                            handleInputChange("birthDay", e.target.value)
                          }
                          className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                        >
                          <option value="">{t.day}</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (day) => (
                              <option
                                key={day}
                                value={day.toString().padStart(2, "0")}
                              >
                                {day}
                              </option>
                            ),
                          )}
                        </select>
                      )}
                      {field === "month" && (
                        <select
                          name="birthMonth"
                          value={profileData.birthMonth}
                          onChange={(e) =>
                            handleInputChange("birthMonth", e.target.value)
                          }
                          className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                        >
                          <option value="">{t.month}</option>
                          <option value="01">{t.january}</option>
                          <option value="02">{t.february}</option>
                          <option value="03">{t.march}</option>
                          <option value="04">{t.april}</option>
                          <option value="05">{t.may}</option>
                          <option value="06">{t.june}</option>
                          <option value="07">{t.july}</option>
                          <option value="08">{t.august}</option>
                          <option value="09">{t.september}</option>
                          <option value="10">{t.october}</option>
                          <option value="11">{t.november}</option>
                          <option value="12">{t.december}</option>
                        </select>
                      )}
                      {field === "year" && (
                        <select
                          name="birthYear"
                          value={profileData.birthYear}
                          onChange={(e) =>
                            handleInputChange("birthYear", e.target.value)
                          }
                          className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                        >
                          <option value="">{t.year}</option>
                          {Array.from(
                            { length: 100 },
                            (_, i) => new Date().getFullYear() - i,
                          ).map((year) => (
                            <option key={year} value={year.toString()}>
                              {year}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <p className="text-gray-900">
                      {combineBirthDate()
                        ? fmt.date(combineBirthDate())
                        : t.notSpecified}
                    </p>
                  </div>
                  {combineBirthDate() && (
                    <div className="ml-6">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-sky-100 text-sky-800">
                        {currentAge} {t.yearsOld}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.gender}
              </label>
              {isEditing ? (
                <select
                  name="gender"
                  value={profileData.gender || ""}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                >
                  <option value="">{t.pleaseSelect}</option>
                  <option value="male">{t.male}</option>
                  <option value="female">{t.female}</option>
                  <option value="other">{t.other}</option>
                  <option value="unknown">{t.preferNotToSay}</option>
                </select>
              ) : (
                <div className="flex items-center space-x-2 py-2">
                  <p className="text-gray-900">
                    {profileData.gender === "male" && t.male}
                    {profileData.gender === "female" && t.female}
                    {profileData.gender === "other" && t.other}
                    {profileData.gender === "unknown" && t.preferNotToSay}
                    {!profileData.gender && t.notSpecified}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Nationality in separate row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.nationality}
              </label>
              {isEditing ? (
                <select
                  name="nationality"
                  value={profileData.nationality}
                  onChange={(e) =>
                    handleInputChange("nationality", e.target.value)
                  }
                  className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                >
                  <option value="">{t.pleaseSelect}</option>
                  {NATIONALITIES.map((nat) => (
                    <option key={nat} value={nat}>
                      {(t[`nationality_${nat}` as keyof typeof t] as
                        | string
                        | undefined) || nat}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex items-center space-x-2 py-2">
                  <p className="text-gray-900">
                    {profileData.nationality
                      ? (t[
                          `nationality_${profileData.nationality}` as keyof typeof t
                        ] as string | undefined) || profileData.nationality
                      : t.notSpecified}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          {(isEditing ||
            profileData.street ||
            profileData.city ||
            profileData.country) && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t.address}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Street and house number */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.street}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="street"
                      value={profileData.street}
                      onChange={(e) =>
                        handleInputChange("street", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder={t.streetName}
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {profileData.street || "-"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.houseNumber}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="houseNumber"
                      value={profileData.houseNumber}
                      onChange={(e) =>
                        handleInputChange("houseNumber", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder={t.houseNo}
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {profileData.houseNumber || "-"}
                    </p>
                  )}
                </div>

                {/* Address line 2 */}
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.addressSupplement}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="addressSupplement"
                      value={profileData.addressSupplement}
                      onChange={(e) =>
                        handleInputChange("addressSupplement", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder={t.apartmentFloorEtc}
                    />
                  ) : (
                    profileData.addressSupplement && (
                      <p className="text-gray-900 py-2">
                        {profileData.addressSupplement}
                      </p>
                    )
                  )}
                </div>

                {/* Postal code and city */}
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.postalCode}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="postalCode"
                      value={profileData.postalCode}
                      onChange={(e) =>
                        handleInputChange("postalCode", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder={t.postalCodePlaceholder}
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {profileData.postalCode || "-"}
                    </p>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.city}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="city"
                      value={profileData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder={t.cityPlaceholder}
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {profileData.city || "-"}
                    </p>
                  )}
                </div>

                {/* State/province - only for some countries */}
                {STATE_OPTIONS[profileData.country] && (
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.state}
                    </label>
                    {isEditing ? (
                      <select
                        name="state"
                        value={profileData.state}
                        onChange={(e) =>
                          handleInputChange("state", e.target.value)
                        }
                        className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                      >
                        <option value="">{t.pleaseSelect}</option>
                        {(STATE_OPTIONS[profileData.country] || []).map(
                          (state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ),
                        )}
                      </select>
                    ) : (
                      <p className="text-gray-900 py-2">
                        {profileData.state || "-"}
                      </p>
                    )}
                  </div>
                )}

                <div
                  className={
                    STATE_OPTIONS[profileData.country]
                      ? "md:col-span-1"
                      : "md:col-span-1"
                  }
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.country}
                  </label>
                  {isEditing ? (
                    <select
                      name="country"
                      value={profileData.country}
                      onChange={(e) =>
                        handleInputChange("country", e.target.value)
                      }
                      className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                    >
                      <option value="">{t.pleaseSelect}</option>
                      {COUNTRY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {profileCountryLabel(option.value, t, language)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center space-x-2 py-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <p className="text-gray-900">
                        {profileData.country
                          ? profileCountryLabel(
                              profileData.country,
                              t,
                              language,
                            )
                          : t.notSpecified}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isEditing && (
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t.cancel}
              </button>
              <button
                data-testid="save-profile"
                onClick={handleSave}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>{t.save}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
